import React, { useEffect, useState } from 'react'
import { Button, Col, Divider, Drawer, Input, Row, Typography, Upload, message } from 'antd'
import { UploadOutlined, UserOutlined, HomeOutlined, ShopOutlined } from '@ant-design/icons'

const { Title, Text } = Typography

const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg']
const MAX_LOGO_SIZE_BYTES = 500 * 1024 // 500KB
const DEFAULT_ACCENT_COLOR = '#01c01e'
const DEFAULT_SIDEBAR_BG = '#ddefe8'
const DEFAULT_SIDEBAR_TEXT = '#262626'

function PersonalizacionSidebarDrawer(props) {
  const [sidebarLogo, setSidebarLogo] = useState('')
  const [accentColor, setAccentColor] = useState(DEFAULT_ACCENT_COLOR)
  const [sidebarBackgroundColor, setSidebarBackgroundColor] = useState(DEFAULT_SIDEBAR_BG)
  const [sidebarTextColor, setSidebarTextColor] = useState(DEFAULT_SIDEBAR_TEXT)

  useEffect(() => {
    if (props.visible && props.editData) {
      setSidebarLogo(props.editData.sidebar_logo || '')
      setAccentColor(props.editData.accent_color || DEFAULT_ACCENT_COLOR)
      setSidebarBackgroundColor(props.editData.sidebar_background_color || DEFAULT_SIDEBAR_BG)
      setSidebarTextColor(props.editData.sidebar_text_color || DEFAULT_SIDEBAR_TEXT)
    }
  }, [props.visible, props.editData])

  const onSaveButton = () => {
    props.saveButton(props.editData.id, {
      sidebar_logo: sidebarLogo,
      accent_color: accentColor,
      sidebar_background_color: sidebarBackgroundColor,
      sidebar_text_color: sidebarTextColor,
    })
  }

  return (
    <Drawer placement="right" closable={false} onClose={props.closable} visible={props.visible} width={560}>
      <Title>{`Sidebar y colores — ${props.editData?.name || ''}`}</Title>
      <div style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 12 }}>Se aplica a los usuarios de esta empresa apenas inician sesión.</div>
      <Divider className={'divider-custom-margins-users'} />
      <Row gutter={16} className={'section-space-field'}>
        <Col xs={24} sm={24} md={24} lg={24}>
          <div className={'title-space-field'}>Logo del sidebar</div>
          <div style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 8 }}>Se muestra en el menú lateral de la app para los usuarios de esta empresa. Si no se carga uno, se usa el logo por defecto de Tendero.</div>
          <Upload
            accept="image/png,image/jpeg"
            showUploadList={false}
            beforeUpload={file => {
              if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
                message.error('El logo debe ser una imagen PNG o JPG')
                return Upload.LIST_IGNORE
              }
              if (file.size > MAX_LOGO_SIZE_BYTES) {
                message.error('El logo no puede pesar más de 500KB')
                return Upload.LIST_IGNORE
              }

              const reader = new FileReader()
              reader.onload = () => setSidebarLogo(reader.result)
              reader.readAsDataURL(file)
              return false
            }}
          >
            <Button icon={<UploadOutlined />}>{sidebarLogo ? 'Cambiar logo' : 'Subir logo'}</Button>
          </Upload>
          {sidebarLogo && (
            <div style={{ marginTop: 12 }}>
              <img src={sidebarLogo} alt="Logo del sidebar" style={{ maxHeight: '55px' }} />
              <div>
                <Button type="link" danger onClick={() => setSidebarLogo('')}>Quitar logo</Button>
              </div>
            </div>
          )}
        </Col>
        <Col xs={24} sm={24} md={24} lg={24} className={'section-space-field'}>
          <div className={'title-space-field'}>Color de acento</div>
          <div style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 8 }}>Reemplaza el verde por defecto en botones, íconos y resaltados de toda la app para los usuarios de esta empresa.</div>
          <Row align="middle" gutter={8}>
            <Col>
              <input
                type="color"
                value={accentColor}
                onChange={e => setAccentColor(e.target.value)}
                style={{ width: 48, height: 40, padding: 0, border: '1px solid #d9d9d9', borderRadius: 4, cursor: 'pointer' }}
              />
            </Col>
            <Col flex="160px">
              <Input value={accentColor} onChange={e => setAccentColor(e.target.value)} maxLength={20} placeholder={DEFAULT_ACCENT_COLOR} />
            </Col>
          </Row>
        </Col>
        <Col xs={24} sm={24} md={24} lg={24} className={'section-space-field'}>
          <div className={'title-space-field'}>Color de fondo del sidebar</div>
          <div style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 8 }}>Reemplaza el fondo claro por defecto del menú lateral para los usuarios de esta empresa.</div>
          <Row align="middle" gutter={8}>
            <Col>
              <input
                type="color"
                value={sidebarBackgroundColor}
                onChange={e => setSidebarBackgroundColor(e.target.value)}
                style={{ width: 48, height: 40, padding: 0, border: '1px solid #d9d9d9', borderRadius: 4, cursor: 'pointer' }}
              />
            </Col>
            <Col flex="160px">
              <Input value={sidebarBackgroundColor} onChange={e => setSidebarBackgroundColor(e.target.value)} maxLength={20} placeholder={DEFAULT_SIDEBAR_BG} />
            </Col>
          </Row>
        </Col>
        <Col xs={24} sm={24} md={24} lg={24} className={'section-space-field'}>
          <div className={'title-space-field'}>Color de letra</div>
          <div style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 8 }}>Reemplaza el color de texto e íconos por defecto del menú lateral y de los botones con el color de acento (ej. "Cobrar", "Cerrar turno") para los usuarios de esta empresa. Útil cuando el color de fondo/acento es oscuro o muy claro.</div>
          <Row align="middle" gutter={8}>
            <Col>
              <input
                type="color"
                value={sidebarTextColor}
                onChange={e => setSidebarTextColor(e.target.value)}
                style={{ width: 48, height: 40, padding: 0, border: '1px solid #d9d9d9', borderRadius: 4, cursor: 'pointer' }}
              />
            </Col>
            <Col flex="160px">
              <Input value={sidebarTextColor} onChange={e => setSidebarTextColor(e.target.value)} maxLength={20} placeholder={DEFAULT_SIDEBAR_TEXT} />
            </Col>
          </Row>
        </Col>
      </Row>
      <Divider className={'divider-custom-margins-users'} />
      <Row gutter={16} className={'section-space-field'}>
        <Col xs={24} sm={24} md={24} lg={24}>
          <div className={'title-space-field'}>Vista previa</div>
          <div style={{ color: 'rgba(0, 0, 0, 0.45)', marginBottom: 8 }}>Así se va a ver el sidebar con estos valores, antes de guardar.</div>
          <div
            style={{
              width: 220,
              border: '1px solid #d9d9d9',
              borderRadius: 8,
              overflow: 'hidden',
              background: sidebarBackgroundColor,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 48,
                padding: '0 8px',
                borderBottom: '1px solid rgba(0,0,0,0.08)',
              }}
            >
              {sidebarLogo ? (
                <img src={sidebarLogo} alt="Logo del sidebar" style={{ maxHeight: 32, maxWidth: '100%' }} />
              ) : (
                <Text type="secondary" style={{ fontSize: 12 }}>Logo por defecto de Tendero</Text>
              )}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px', borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
              <UserOutlined style={{ fontSize: 16, color: sidebarTextColor }} />
              <Text style={{ fontSize: 12, color: sidebarTextColor }}>Nombre de usuario</Text>
            </div>
            <div style={{ padding: 8 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '8px 12px',
                  borderRadius: 10,
                  background: accentColor,
                  color: sidebarTextColor,
                  marginBottom: 6,
                }}
              >
                <HomeOutlined style={{ fontSize: 14, color: sidebarTextColor }} />
                <Text style={{ fontSize: 12, color: sidebarTextColor }}>Empresas</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px' }}>
                <ShopOutlined style={{ fontSize: 14, color: accentColor }} />
                <Text style={{ fontSize: 12, color: sidebarTextColor }}>Tiendas</Text>
              </div>
            </div>
          </div>
        </Col>
      </Row>
      <Divider className={'divider-custom-margins-users'} />
      <Row gutter={16}>
        <Col xs={24} sm={24} md={24} lg={24}>
          <div className="text-right">
            <Button type="link" className="cancel-button" onClick={() => props.cancelButton()}>
              Cancelar
            </Button>
            <Button htmlType="submit" className="title-tendero new-button" onClick={onSaveButton}>
              Guardar
            </Button>
          </div>
        </Col>
      </Row>
    </Drawer>
  )
}

export default PersonalizacionSidebarDrawer
